import { ref, onMounted, onUnmounted } from 'vue';

interface AnimationOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useScrollAnimation(options: AnimationOptions = {}) {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    once = true,
  } = options;

  const animatedElements = ref<Set<Element>>(new Set());

  const observer = ref<IntersectionObserver | null>(null);

  const initObserver = () => {
    if (typeof window === 'undefined') { return; }

    observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
            if (once) {
              animatedElements.value.add(entry.target);
            }
          } else if (!once) {
            entry.target.classList.remove('animated');
          }
        });
      },
      {
        threshold,
        rootMargin,
      },
    );
  };

  const observe = (element: Element) => {
    if (observer.value && element) {
      observer.value.observe(element);
    }
  };

  const unobserve = (element: Element) => {
    if (observer.value && element) {
      observer.value.unobserve(element);
      animatedElements.value.delete(element);
    }
  };

  onMounted(() => {
    initObserver();
  });

  onUnmounted(() => {
    if (observer.value) {
      observer.value.disconnect();
    }
  });

  return {
    observe,
    unobserve,
  };
}

export function usePrefersReducedMotion() {
  const prefersReducedMotion = ref(false);

  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.value = mediaQuery.matches;

    const handleChange = (e: MediaQueryListEvent) => {
      prefersReducedMotion.value = e.matches;
    };

    mediaQuery.addEventListener('change', handleChange);

    onUnmounted(() => {
      mediaQuery.removeEventListener('change', handleChange);
    });
  }

  return prefersReducedMotion;
}
